import { useSignalEffect } from "@preact/signals-react";
import { log } from "./logger";
import { client } from "./nats-client";
import { getTenantConfig } from "./tenantUtils";

const tag = "finapp";

let finctx: any = null;
let isRegistered = false;

export function useFinApp(onSearch, onLinkSymbolClick, onLinkAlert, onAnalytics) {
  useSignalEffect(() => {
    if (isRegistered) {
      log.debug(tag, `🟡 useFinApp already registered, skipping`);
      return;
    }

    log.debug(tag, `🟢 useFinApp starting`);

    (async () => {
      isRegistered = true;

      try {
        const tenantConfig = getTenantConfig();
        const compUrlResult = tenantConfig.finappsWebComponentUrl;
        
        if (!compUrlResult) {
          log.error(tag, "Failed to get finapp component url", { compUrl: compUrlResult });
          return;
        }
        const compUrl = compUrlResult;

        const m = await import(compUrl);
        let token;
        let renewingTokenPromise: Promise<any> | null;

        const theme = getTheme();

        const getToken = async () => {
          if (!renewingTokenPromise) {
            renewingTokenPromise = (async () => {
              try {
                token = await requestToken();
              } finally {
                renewingTokenPromise = null;
              }
              return token;
            })();
          }
          return renewingTokenPromise;
        };

        const tokenProvider = (renewToken) => (renewToken || token === undefined ? getToken() : token);
        const renewToken = getToken;

        finctx = await m.registerFinApps({
          tokenProvider,
          renewToken,
          attributes: {
            theme,
          },
          disableAnalytics: true,
        });

        log.info(tag, "Fin App Context", finctx);

        log.debug(tag, `🔵 useFinApp adding event listeners`);

        // Create named functions to track them
        const searchHandler = (e) => {
          log.debug(tag, `🔍 useFinApp SEARCH event received by instance`);
          const data = e?.detail?.data;
          if (!data) return;
          if (onSearch) onSearch(data);
        };

        const analyticsHandler = (e) => {
          log.debug(tag, `📊 useFinApp ANALYTICS event received`, {
            timestamp: Date.now(),
            eventDetail: e?.detail?.data,
            composedPath: e?.composedPath(),
          });
          const data = e?.detail?.data;
          if (!data) return;
          if (onAnalytics) onAnalytics(data, e);
        };

        const linkSymbolHandler = (e) => {
          log.debug(tag, `🔗 useFinApp LINK-SYMBOL event received`);
          const data = e?.detail?.data;
          if (!data) return;
          if (onLinkSymbolClick)
            onLinkSymbolClick(data, ((e.srcElement as Element).parentElement as Element).getAttribute("data-testid"));
        };

        const linkAlertHandler = (e) => {
          log.debug(tag, `🚨 useFinApp LINK-ALERT event received`);
          const data = e?.detail?.data;
          if (!data) return;
          if (onLinkAlert)
            onLinkAlert(data, ((e.srcElement as Element).parentElement as Element).getAttribute("data-testid"));
        };

        finctx.addEventListener("search-selection", searchHandler);
        finctx.addEventListener("market-apps-analytics", analyticsHandler);
        finctx.addEventListener("link-symbol", linkSymbolHandler);
        finctx.addEventListener("link-alert", linkAlertHandler);

        log.debug(tag, `✅ useFinApp instance`);
      } catch (ex) {
        isRegistered = false;
        log.error(tag, "Failed to load fin apps", ex);
      }
    })();
  });
}

function getTheme() {
  return document.body.classList.contains("dark-theme") ? "dark" : "light";
}

async function requestToken() {
  const finAppTokenCreate = await client.request<{ Token: string }>("finapptoken.create", {});

  if (!finAppTokenCreate.success || finAppTokenCreate.data === undefined) {
    log.error(tag, "finapp create token failed", { finAppTokenCreate });

    // Temporary token, Fincentric auth is only setup for Communify Test
    return "DCvt8Kk1xeYn834f3qDVRtHGmmv0"; // Expires 2/12/2025 10:52:12 PM (ET)
  }

  return finAppTokenCreate.data.Token;
}
